import { forwardRef, useEffect, useId, useMemo, useRef, useState, type ChangeEvent } from 'react';
import { Button } from '../ui/Button';
import { Combobox, type ComboboxOption } from '../ui/Combobox';
import { Input } from '../ui/Input';
import { FieldLabel } from './FieldLabel';
import { useCountries } from '@/hooks/useCountries';
import { PHONE_MAX_NATIONAL_DIGITS, PHONE_STRINGS } from '@/lib/constants/phone';
import {
  composePhone,
  dialCodeFor,
  formatNationalDigits,
  isPhoneCountry,
  parsePastedPhone,
  parsePhone,
} from '@/lib/utils/phone.utils';
import { caretIndexForDigits } from '@/lib/utils/numeric.utils';

const NON_DIGITS = /\D/g;

export interface PhoneFieldProps {
  /** Visible label above the field; also the accessible name of the number input. */
  label: string;
  /**
   * The form value: one E.164 string (`+14155550142`), or `''` when empty. While the user is
   * mid-entry it can be a `+<dial code><digits>` candidate that is not valid yet; validate with
   * `phoneSchema` (`lib/utils/phone.utils.ts`).
   */
  value: string | null | undefined;
  /** Receives the E.164 string (or `''`) on every change; `field.onChange` from `Controller` fits. */
  onChange: (value: string) => void;
  onBlur?: () => void;
  name?: string;
  error?: string;
  required?: boolean;
  optional?: boolean;
  labelVariant?: 'bold' | 'caps';
  disabled?: boolean;
  /** `iso2` of the user's country when known; the picker starts there. Defaults to the first country in the list. */
  defaultCountryIso2?: string | null;
  /**
   * A phone saved earlier as free text that could not be read as a number
   * (`toPhoneFormFields(...).legacyPhone`). Shown beside the field until the user types a new one.
   */
  legacyText?: string | null;
  /**
   * Shows a "Remove" action beside the legacy text; called when the user removes it, so the form
   * can drop `legacyPhone` and save the phone as cleared (`phonePayload(..., savedPhone)` then
   * sends `null`). Omit it where the phone cannot be removed (a required phone).
   */
  onClearLegacy?: () => void;
  /**
   * Inset look (bordered shell, small teal label inside) to sit beside `TextField inset` fields,
   * as on the public application form.
   */
  inset?: boolean;
}

/**
 * Phone input with a searchable country picker (dial code beside each country) and a digits
 * input (MYN-277, FR-184). Dial codes come from `libphonenumber-js`, keyed by each country's
 * `iso2`; countries the library has no dial code for are not listed. The form holds one E.164
 * string. Typing or pasting a `+` number sets the picker to its country as soon as the code is readable. The picker and the value never disagree: a country change under typed digits recomposes the number under the new dial code. Controlled: bind it with
 * `Controller` as `<PhoneField {...field} label=... />`.
 */
export const PhoneField = forwardRef<HTMLInputElement, PhoneFieldProps>(function PhoneField(
  {
    label,
    value,
    onChange,
    onBlur,
    name,
    error,
    required = false,
    optional = false,
    labelVariant = 'caps',
    disabled = false,
    defaultCountryIso2,
    legacyText,
    onClearLegacy,
    inset = false,
  },
  ref,
) {
  const inputId = useId();
  const { data: countries = [], isLoading } = useCountries();
  const dialCountries = useMemo(
    () => countries.filter((country) => isPhoneCountry(country.iso2)),
    [countries],
  );

  const [selectedIso2, setSelectedIso2] = useState<string | undefined>(
    () => parsePhone(value, defaultCountryIso2)?.iso2,
  );
  const [digits, setDigits] = useState(() => parsePhone(value, defaultCountryIso2)?.nationalNumber ?? '');
  const [query, setQuery] = useState('');
  // A "+" number still being typed whose country code is not complete yet (`+4`): kept as typed.
  const [pendingIntl, setPendingIntl] = useState<string | undefined>(undefined);
  const lastEmitted = useRef(value ?? '');

  // A value changed from outside (form reset, a refetched profile): follow it.
  useEffect(() => {
    const current = value ?? '';
    if (current === lastEmitted.current) return;
    lastEmitted.current = current;
    const parts = parsePhone(current, defaultCountryIso2);
    setPendingIntl(undefined);
    setDigits(parts?.nationalNumber ?? '');
    if (parts) setSelectedIso2(parts.iso2);
  }, [value, defaultCountryIso2]);

  const fallbackIso2 =
    dialCountries.find((country) => country.iso2.toUpperCase() === defaultCountryIso2?.toUpperCase())
      ?.iso2 ?? dialCountries[0]?.iso2;
  const activeIso2 = selectedIso2 ?? fallbackIso2;
  const activeCountry = dialCountries.find(
    (country) => country.iso2.toUpperCase() === activeIso2?.toUpperCase(),
  );

  // The picker and the saved value must never disagree: when the effective country changes under
  // typed digits (the default country followed a new profile country, or the country list just
  // loaded), re-emit the number under the new dial code.
  const previousIso2 = useRef(activeIso2);
  useEffect(() => {
    if (previousIso2.current === activeIso2) return;
    previousIso2.current = activeIso2;
    if (digits === '') return;
    const next = composePhone(activeIso2, digits);
    if (next === lastEmitted.current) return;
    lastEmitted.current = next;
    onChange(next);
  }, [activeIso2, digits, onChange]);

  const options: ComboboxOption[] = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return dialCountries
      .map((country) => ({
        value: country.iso2,
        label: `${country.name} (${dialCodeFor(country.iso2)})`,
      }))
      .filter((option) => option.label.toLowerCase().includes(needle));
  }, [dialCountries, query]);

  const emit = (iso2: string | undefined, nextDigits: string) => {
    const next = composePhone(iso2, nextDigits);
    lastEmitted.current = next;
    onChange(next);
  };

  const handleNumberChange = (event: ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value;
    const plusAt = raw.indexOf('+');
    if (plusAt >= 0) {
      // An international number, typed or pasted (also into a field that already has digits):
      // the leading `+<code>` picks the country as soon as it is readable.
      const international = `+${raw.slice(plusAt + 1).replace(NON_DIGITS, '')}`;
      const parts = parsePastedPhone(international, activeIso2);
      if (parts) {
        setPendingIntl(undefined);
        setSelectedIso2(parts.iso2);
        setDigits(parts.nationalNumber);
        emit(parts.iso2, parts.nationalNumber);
      } else {
        setPendingIntl(international);
        setDigits('');
        emit(activeIso2, '');
      }
      return;
    }
    setPendingIntl(undefined);
    const input = event.target;
    const caret = input.selectionStart ?? raw.length;
    const caretDigits = raw.slice(0, caret).replace(NON_DIGITS, '').length;
    const next = raw.replace(NON_DIGITS, '').slice(0, PHONE_MAX_NATIONAL_DIGITS);
    setDigits(next);
    emit(activeIso2, next);
    // Live formatting (MYN-301, FR-218): write the formatted text and restore
    // the caret by digit count, the same technique `useNumericInput` uses for
    // the money fields (FR-183) - the next render's controlled `value` will
    // already match what is written here, so React leaves this alone.
    const shown = formatNationalDigits(activeIso2, next);
    input.value = shown;
    const position = caretIndexForDigits(shown, Math.min(caretDigits, next.length));
    input.setSelectionRange(position, position);
  };

  const handleCountrySelect = (option: ComboboxOption) => {
    setSelectedIso2(option.value);
    setPendingIntl(undefined);
    setQuery('');
    // With digits typed, the number is recomposed under the new dial code; with none, the
    // country is kept here and the next digits typed compose with it.
    emit(option.value, digits);
  };

  const picker = (
    <Combobox
      label={PHONE_STRINGS.COUNTRY_PICKER_LABEL}
      inputValue={query}
      onInputChange={setQuery}
      selectedLabel={
        activeCountry ? `${activeCountry.name} (${dialCodeFor(activeCountry.iso2)})` : undefined
      }
      options={options}
      onSelect={handleCountrySelect}
      loading={isLoading}
      disabled={disabled}
      placeholder={PHONE_STRINGS.COUNTRY_PICKER_PLACEHOLDER}
    />
  );
  const numberInput = (
    <Input
      ref={ref}
      id={inputId}
      name={name}
      label={label}
      type="tel"
      inputMode="tel"
      autoComplete="tel-national"
      variant={inset ? 'bare' : 'default'}
      value={pendingIntl ?? formatNationalDigits(activeIso2, digits)}
      onChange={handleNumberChange}
      onBlur={onBlur}
      disabled={disabled}
      placeholder={PHONE_STRINGS.NUMBER_PLACEHOLDER}
      error={error}
    />
  );
  const showLegacy = Boolean(legacyText) && digits === '' && pendingIntl === undefined;
  const legacyNote = showLegacy && (
    <p className="flex items-center gap-2 pl-1 text-xs text-slate">
      <span>
        {PHONE_STRINGS.LEGACY_NOTE}: <span className="text-navy">{legacyText}</span>
      </span>
      {onClearLegacy && (
        <Button
          type="button"
          variant="link-sm"
          aria-label={PHONE_STRINGS.LEGACY_REMOVE_LABEL}
          onClick={onClearLegacy}
          disabled={disabled}
        >
          {PHONE_STRINGS.LEGACY_REMOVE}
        </Button>
      )}
    </p>
  );
  const pickerAndNumber = (
    <div className="grid gap-3 sm:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
      {picker}
      {numberInput}
    </div>
  );

  if (inset) {
    return (
      <div className="flex flex-col gap-1">
        <div className="rounded-button border border-input-border bg-white px-4 py-2.5 focus-within:border-transparent focus-within:ring-2 focus-within:ring-dkgreen">
          <label htmlFor={inputId} className="font-sans text-xs font-semibold text-teal2">
            {label}
            {required && <span aria-hidden="true">*</span>}
          </label>
          {pickerAndNumber}
        </div>
        {error && (
          <p role="alert" className="pl-1 text-xs text-danger">
            {error}
          </p>
        )}
        {legacyNote}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1.5">
      <FieldLabel
        label={label}
        htmlFor={inputId}
        required={required}
        optional={optional}
        variant={labelVariant}
      />
      {pickerAndNumber}
      {legacyNote}
    </div>
  );
});

PhoneField.displayName = 'PhoneField';
